import { styled } from '@mui/material/styles';
import { IconButton as MuiIconButton } from '@mui/material';
import { IconButton as FplIconButton, type IconButtonProps as FplIconButtonProps } from '@figma/fpl-components';
import { forwardRef } from 'react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { getFplDataAttributes } from './fplDataAttributes';
import type { IconButtonProps, Theme, SxProps } from '@mui/material';

export interface AppIconButtonProps extends Omit<IconButtonProps, 'color' | 'sx' | 'classes'> {
  width?: string | number;
  height?: string | number;
  mode?: 'on-light' | 'on-dark';
  sx?: SxProps<Theme>;
  /** Corner shape of the FPL button; the legacy button keeps its own. */
  shape?: FplIconButtonProps['shape'];
}

type FplIconButtonForwardedProps = Omit<FplIconButtonProps, 'children' | 'shape' | 'size' | 'variant'>;

const getHoverBackgroundColor = (mode: AppIconButtonProps['mode']) => {
  switch (mode) {
    case 'on-light':
      return color.White16_T;
    case 'on-dark':
      return color.Black84;
  }
};

const StyledIconButton = styled(MuiIconButton, {
  // oxlint-disable-next-line typescript/no-unnecessary-type-assertion -- MUI types this prop as PropertyKey.
  shouldForwardProp: (prop) => !['width', 'height', 'mode'].includes(prop as string),
})<AppIconButtonProps>(({ width = 28, height = 28, mode = 'on-dark' }) => ({
  minWidth: width,
  minHeight: height,
  width,
  height,
  padding: 0,
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  color: token.text,
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: getHoverBackgroundColor(mode),
    color: token.text,
  },

  '&:disabled': {
    cursor: 'not-allowed',
    color: token.textSecondary,
    backgroundColor: 'transparent',
  },

  '& .MuiTouchRipple-root': {
    display: 'none',
  },
}));

const getNumericSize = (value: string | number | undefined) => {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const getFplIconButtonSize = ({
  width,
  height,
  size,
}: Pick<AppIconButtonProps, 'width' | 'height' | 'size'>): NonNullable<FplIconButtonProps['size']> => {
  const numericSize = getNumericSize(width) ?? getNumericSize(height);

  if (numericSize != null) {
    return numericSize >= 32 ? 'lg' : 'md';
  }

  return size === 'large' ? 'lg' : 'md';
};

export const AppIconButton = forwardRef<HTMLButtonElement, AppIconButtonProps>(
  ({ children, width, height, mode, sx, shape = 'square', ...props }, ref) => {
    const isFplMigrationEnabled = useFplMigrationFlag();

    if (isFplMigrationEnabled) {
      const {
        'aria-controls': ariaControls,
        'aria-current': ariaCurrent,
        'aria-describedby': ariaDescribedBy,
        'aria-disabled': ariaDisabled,
        'aria-expanded': ariaExpanded,
        'aria-haspopup': ariaHasPopup,
        'aria-keyshortcuts': ariaKeyShortcuts,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        autoFocus,
        className: _className,
        disabled,
        disableFocusRipple: _disableFocusRipple,
        disableRipple: _disableRipple,
        disableTouchRipple: _disableTouchRipple,
        edge: _edge,
        focusRipple: _focusRipple,
        form,
        formAction,
        formEncType,
        formMethod,
        formNoValidate,
        formTarget,
        id,
        name,
        onBlur,
        onClick,
        onFocus,
        onKeyDown,
        onKeyUp,
        onMouseDown,
        onMouseEnter,
        onMouseLeave,
        onPointerCancel,
        onPointerDown,
        onPointerEnter,
        onPointerLeave,
        onPointerMove,
        onPointerOut,
        onPointerOver,
        onPointerUp,
        role,
        size,
        style: _style,
        tabIndex,
        title,
        TouchRippleProps: _TouchRippleProps,
        type,
        ..._unsupportedFplIconButtonProps
      } = props;
      const forwardedFplIconButtonProps: FplIconButtonForwardedProps = {
        ...getFplDataAttributes(props),
        'aria-controls': ariaControls,
        'aria-current': ariaCurrent,
        'aria-describedby': ariaDescribedBy,
        'aria-disabled': ariaDisabled === true || ariaDisabled === 'true' ? true : undefined,
        'aria-expanded': ariaExpanded,
        'aria-haspopup': ariaHasPopup,
        'aria-keyshortcuts': ariaKeyShortcuts,
        'aria-label': ariaLabel ?? title ?? '',
        'aria-labelledby': ariaLabelledBy,
        autoFocus,
        disabled,
        form,
        formAction,
        formEncType,
        formMethod,
        formNoValidate,
        formTarget,
        id,
        name,
        onBlur,
        onClick,
        onFocus,
        onKeyDown,
        onKeyUp,
        onMouseDown,
        onMouseEnter,
        onMouseLeave,
        onPointerCancel,
        onPointerDown,
        onPointerEnter,
        onPointerLeave,
        onPointerMove,
        onPointerOut,
        onPointerOver,
        onPointerUp,
        role,
        tabIndex,
        title,
        type,
      };

      return (
        <FplIconButton
          {...forwardedFplIconButtonProps}
          ref={ref}
          shape={shape}
          size={getFplIconButtonSize({ width, height, size })}
          variant="ghost"
        >
          {/* fpl-migration - keeps legacy icons that rely on inherited icon color working in FPL. */}
          <span style={{ color: 'var(--color-icon)', display: 'contents' }}>{children}</span>
        </FplIconButton>
      );
    }

    return (
      <StyledIconButton ref={ref} width={width} height={height} mode={mode} disableRipple sx={sx} {...props}>
        {children}
      </StyledIconButton>
    );
  },
);

AppIconButton.displayName = 'AppIconButton';
